import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Card, CardBody, Col, Row, Container, Label, Input, Button, Form, Alert } from "reactstrap";

import viewIcon from "../../assets/images/view_icon.png"; // Replace with your actual icon
import downloadIcon from "../../assets/images/download_icon.png";
// import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css"; // Flatpickr styles

const token = sessionStorage.getItem("authToken");


const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return isNaN(date) ? dateString : format(date, "PPP"); // Example: "Mar 5, 2025"
};

const isDateField = (key) =>
  ["created_at", "effective_from", "expiry_date", "updatedAt", "updated_at"].includes(key.toLowerCase());

const toProperCase = (word) =>
  typeof word === "string" || typeof word === "number"
    ? word.toString().charAt(0).toUpperCase() + word.toString().slice(1).toLowerCase()
    : "";

const ViewContractPage = () => {
  const { type } = useParams();
  const documentType = type;

  const [searchParams] = useSearchParams();
  const isHome = searchParams.get("h") === "true";
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const documentId = useParams();
  const [documentData, setDocumentData] = useState(null);
  const [userName, setUserName] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    reference: "",
    country: "",
    contracting_party: "",
    contract_status: "",
    document_type: "",
    created_by: "",
    created_at: "",
    effective_from: "",
    notification_period: "",
    expiry_date: "",
    comment: "", // Add comment field
  });
  const [isHovered, setIsHovered] = useState(false);

  // Fetch and set contract data
  useEffect(() => {
    const fetchDocumentData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_BASE_URL || "";
        if (!apiUrl) {
          console.error("API base URL is not configured.");
          return;
        }

        const response = await fetch(`${apiUrl}/api/contract/${documentId.id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        setDocumentData(data.contract_document[0]);
        setFormData({
          title: data.title || "",
          reference: data.reference || "",
          country: data.country || "",
          contracting_party: data.contracting_party || "",
          contract_status: data.status || "",
          document_type: data.document_type || "",
          created_by: data.created_by || "",
          created_at: data.createdAt || "",
          effective_from: data.effective_from || "",
          notification_period: data.notification_period || "",
          expiry_date: data.expiry_date || "",
          comment: data.comment || "", // Include comments in form data
        });
      } catch (error) {
        console.error("Error fetching document data:", error);
      }
    };

    if (documentId) {
      fetchDocumentData();
    }
  }, [documentId]);

  // Fetch and set the username
  useEffect(() => {
    if (formData.created_by) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/${formData.created_by}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => setUserName(data.username))
        .catch((error) => {
          console.error("Error fetching user:", error);
          setUserName(formData.created_by);
        });
    }
  }, [formData.created_by]);

  useEffect(() => {
    const errInterval = setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);

    return () => clearTimeout(errInterval);
  }, [success, error]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          {/* Alerts */}
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          <Row>
            <Col md={6}>
              <Card>
                <CardBody>
                  <Form>
                    {Object.keys(formData).map((key) => (
                      <Row className="mb-3 align-items-center" key={key}>
                        <>
                          <Label htmlFor={key} className="col-md-4 col-form-label text-md-right p-0 m-0">
                            {key
                              .trim()
                              .replace("_", " ")
                              .split(" ")
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(" ")}
                          </Label>

                          <div className="col-md-8">
                            <Input
                              type={key === "comment" ? "textarea" : "text"} // Use textarea for comments
                              name={key}
                              id={key}
                              className="form-control w-100"
                              value={
                                isDateField(key)
                                  ? formatDate(formData[key])
                                  : key === "created_by"
                                  ? userName || "Loading"
                                  : formData[key]
                              }
                              readOnly={true}
                            />
                          </div>
                        </>
                      </Row>
                    ))}
                  </Form>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <CardBody>
                  {documentData ? (
                    documentData.type === "image" ? (
                      <div
                        className={`image-container ${isHovered ? "hovered" : ""}`}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      >
                        {/* Image */}
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${documentData.location}`}
                          alt="Document Viewer"
                          className="image"
                          width="600px"
                        />

                        {/* Icons (Show on Hover) */}
                        {isHovered && (
                          <div className="icon-overlay">
                            <a
                              href={`${process.env.REACT_APP_API_BASE_URL}/uploads/${documentData.location}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img src={viewIcon} alt="View" className="icon fw-bold" width={"10dp"} />
                            </a>

                            <a
                              href={`${process.env.REACT_APP_API_BASE_URL}/download/${documentData.location}/${JSON.parse(sessionStorage.getItem("authUser"))._id}`}
                              download
                            >
                              <img src={downloadIcon} alt="Download" className="icon" />
                            </a>
                          </div>
                        )}
                      </div>
                    ) : documentData.type === "word" || documentData.type === "pdf" || documentData.type === "word" ? (
                      <>
                        <iframe
                          src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${documentData.location}`}
                          title="Document Viewer"
                          width="100%"
                          height="600px"
                          className="document"
                        ></iframe>
                      </>
                    ) : (
                      <p>Document is of another format</p>
                    )
                  ) : (
                    <p>No {toProperCase(documentType)} Documents Available...</p>
                  )}
                </CardBody>
              </Card>
              <Row className="mb-6">
                <Col md={12} className="text-center">
                  <Button
                    color="primary"
                    onClick={() => navigate(isHome ? "/dashboard" : `/contracts/${documentType}`)}
                  >
                    {`Back to ${isHome ? "dashboard" : toProperCase(documentType) + "s"}`}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ViewContractPage;